import {forwardRef, PropsWithChildren, ReactElement, Ref, useCallback} from 'react'
import {UnnormalizedProps as UnnormalizedStyleProps} from '@yosmanyga/core-style'
import {WithTheme} from '@yosmanyga/theme'
import {NonExistentPrimitiveError, usePrimitives} from '@yosmanyga/primitive-ui-spec'
import Text from './Text'
import Progress from './Progress'
import BaseContainer from './Container'

const Input = forwardRef<
    HTMLInputElement,
    InputWrapperProps & InputEntryProps & UnnormalizedStyleProps
>(({
    start, end, help, error, progress,
    value, placeholder, focus, keyboard, multi, secure, capitalize,
    delay,
    onChange, onSubmit, onEnter, onKeyDown, onFocus,
    height, margin, pseudo,
    testId,
    ...props
}, ref) => {
    return <InputWrapperWithTheme
        start={start}
        end={end}
        help={help}
        error={error}
        progress={progress}
        margin={margin}
        pseudo={pseudo}
        testId={testId}
        {...props}
    >
        <InputEntryWithTheme
            ref={ref}
            value={value}
            placeholder={placeholder}
            keyboard={keyboard}
            delay={delay}
            focus={focus}
            multi={multi}
            secure={secure}
            capitalize={capitalize}
            onChange={onChange}
            onSubmit={onSubmit}
            onEnter={onEnter}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            flex={1} // Without this, the Native version will not focus on pressing inside the wrapper
            height={height}
            pseudo={{
                placeholder: pseudo?.placeholder
            }}
        />
    </InputWrapperWithTheme>
})

export type InputWrapperProps = {
    start?: ReactElement,
    end?: ReactElement | false,
    help?: string,
    error?: string,
    progress?: boolean,

    testId?: string,

    children?: ReactElement
}

const InputWrapper = ({
    start, end, help, error, progress,
    margin, padding, width, border, background,
    children, ...props
}: InputWrapperProps & UnnormalizedStyleProps) => {
    if (progress) {
        end = <Progress width={20}/>
    }

    return <BaseContainer
        flow={{
            direction: 'column',
            wrap: false
        }}
        align={{
            main: 'flex-start'
        }}
        margin={{
            ...margin,
            raw: true,
        }}
        width={width}
        {...props}
    >
        <BaseContainer
            flow='row'
            align={{
                main: 'flex-start',
                cross: 'center'
            }}
            padding={{
                ...padding,
                raw: true,
            }}
            border={border}
            background={background}
        >
            {start && <InputStartWithTheme elem={start}/>}
            {children}
            {end && <InputEndWithTheme elem={end}/>}
        </BaseContainer>
        {help && <InputHelp text={help}/>}
        {error && <InputError text={error}/>}
    </BaseContainer>
}

const InputWrapperWithTheme = (props: PropsWithChildren<InputWrapperProps & UnnormalizedStyleProps>) => <WithTheme
    {...props}
    identifier='input_wrapper'
    Component={InputWrapper}
/>

export type InputEntryProps = {
    ref?: Ref<HTMLInputElement>
    value?: string,
    placeholder?: string,
    keyboard?: 'number' | 'decimal' | 'phone',
    delay?: number,
    focus?: boolean,
    multi?: number,
    secure?: boolean,
    capitalize?: 'none' | 'sentences' | 'words' | 'characters',
    onChange: (value: string) => void,
    onSubmit?: (value: string) => void,
    onEnter?: (value: string) => void,
    onKeyDown?: (key: string) => void,
    onFocus?: (key: string) => void,
}

const InputEntry = forwardRef<
    HTMLInputElement,
    InputEntryProps & UnnormalizedStyleProps
>(({
    value,
    placeholder, keyboard, delay, focus, multi, secure, capitalize,
    onChange, onSubmit, onEnter, onKeyDown,
    ...props
}, ref) => {
    const {Input: BaseInput} = usePrimitives()

    if (!BaseInput) {
        throw new NonExistentPrimitiveError('Input')
    }

    return <BaseInput
        ref={ref}
        value={value}
        placeholder={placeholder}
        keyboard={keyboard}
        delay={delay}
        focus={focus}
        multi={multi}
        secure={secure}
        capitalize={capitalize}
        onChange={onChange}
        onSubmit={onSubmit}
        onEnter={onEnter}
        onKeyDown={onKeyDown}
        {...props}
    />
})

const InputEntryWithTheme = forwardRef<
    HTMLInputElement,
    InputEntryProps & UnnormalizedStyleProps
>((props, ref) => {
    const Component = useCallback((props: InputEntryProps & UnnormalizedStyleProps) => {
        return <InputEntry
            ref={ref}
            {...props}
        />
    }, [])

    return <WithTheme
        {...props}
        identifier='input_entry'
        Component={Component}
    />
})

type InputStartProps = {
    elem: ReactElement
}

const InputStart = (
    {elem, ...props}: InputStartProps & UnnormalizedStyleProps
) => {
    return <elem.type
        {...props}
        {...elem.props}
    />
}

const InputStartWithTheme = (props: InputStartProps & UnnormalizedStyleProps) => <WithTheme
    {...props}
    identifier='input_start'
    Component={InputStart}
/>

type InputEndProps = {
    elem: ReactElement
}

const InputEnd = (
    {elem, ...props}: InputEndProps & UnnormalizedStyleProps
) => {
    return <elem.type
        {...props}
        {...elem.props}
    />
}

const InputEndWithTheme = (props: InputEndProps & UnnormalizedStyleProps) => <WithTheme
    {...props}
    identifier='input_end'
    Component={InputEnd}
/>

type InputHelpProps = {
    text?: string
}

const InputHelp = ({
    text
}: InputHelpProps) => {
    return <Text
        intent='secondary'
        margin={{
            top: 0.5
        }}
    >
        {text}
    </Text>
}

type InputErrorsProps = {
    text?: string
}

const InputError = ({
    text
}: InputErrorsProps) => {
    return <Text
        intent='negative'
        align={{
            main: 'flex-start'
        }}
        margin={{
            top: 0.5
        }}
        padding={{
            top: 0.5,
            bottom: 0.5,
            right: 0.5
        }}
    >
        {text}
    </Text>
}

// const renderHelp = (help) => {
//     return help && (!Array.isArray(help)
//         ? <Text>
//             {help}
//         </Text>
//         : help.map((line, i) => {
//             return <Text
//                 key={i}
//             >
//                 {line}
//             </Text>
//         })
//     )
// }

export {
    Input,
    InputWrapper
}
